import {
  AccessTime as AccessTimeIcon,
  CalendarMonth as CalendarIcon,
  Close as CloseIcon,
  Error as ErrorIcon,
  NextWeek as NextWeekIcon,
  Schedule as ScheduleIcon,
  Today as TodayIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  FormControlLabel,
  IconButton,
  Paper,
  Skeleton,
  Typography,
} from "@mui/material";
import { PhoneMissed as ContactAttemptIcon } from "@mui/icons-material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { skipToken } from "@reduxjs/toolkit/query";
import { addDays, addMinutes, format } from "date-fns";
import { sv } from "date-fns/locale";
import React, { useState } from "react";
import { useUpdateNextActionMutation } from "../../store/services/nextActionService";
import { FreeSlotChips } from "../free-slots";
import { useGetWorkItemQuery } from "../../store/services/workItemService";
import {
  useGetCalendarEventByIdQuery,
  useUpdateCalendarEventMutation,
} from "../../store/ms-services/calendarApi";

interface MoveWorkItemDialogProps {
  open: boolean;
  onClose: () => void;
  workItemNo: number;
  onComplete: () => void;
}

const MoveWorkItemDialog: React.FC<MoveWorkItemDialogProps> = ({
  open,
  onClose,
  workItemNo,
  onComplete,
}) => {
  const [newScheduledDate, setNewScheduledDate] = useState<Date | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registerContactAttempt, setRegisterContactAttempt] = useState(false);
  const [updateCalendarEvent] = useUpdateCalendarEventMutation();
  const {
    data: activity,
    isLoading,
    error: loadingError,
  } = useGetWorkItemQuery(workItemNo ? { workItemId: workItemNo } : skipToken);
  const [updateWorkItem] = useUpdateNextActionMutation();
  const hasExternalId = Boolean(activity?.ExternalId);
  const { data: calendarEvent, error: calendarError } =
    useGetCalendarEventByIdQuery(activity?.ExternalId || "", {
      skip: !hasExternalId,
    });
  
  // Check if calendar event was not found (404 error)
  const isCalendarEventNotFound = 
    calendarError && 
    'status' in calendarError && 
    calendarError.status === 404;

  // Nollställ state varje gång dialogen öppnas så inget släpar med från
  // en tidigare flytt (t.ex. kontaktförsök-kryssrutan eller felmeddelande).
  React.useEffect(() => {
    if (open) {
      setNewScheduledDate(null);
      setRegisterContactAttempt(false);
      setError(null);
    }
  }, [open]);

  // Sätt initial datum när aktiviteten laddas
  React.useEffect(() => {
    if (activity?.ScheduledTo && !newScheduledDate) {
      setNewScheduledDate(new Date(activity.ScheduledTo));
    }
  }, [activity, newScheduledDate]);

  const handleComplete = async () => {
    if (!newScheduledDate) {
      setError("Välj ett nytt datum och tid");
      return;
    }

    const now = new Date();
    if (newScheduledDate < now) {
      setError("Det nya datumet kan inte vara i det förflutna");
      return;
    }

    setIsCompleting(true);
    setError(null);

    try {
      // Ersätt detta med ditt riktiga API-anrop
      if (!activity) {
        setError("Ingen aktivitet hittades");
        return;
      }
      // Only update calendar event if it exists and wasn't deleted
      if (activity.ExternalId && calendarEvent && !isCalendarEventNotFound) {
        try {
          await updateCalendarEvent({
            eventData: {
              start: {
                dateTime: newScheduledDate.toISOString(),
                timeZone: "utc",
              },
              end: {
                dateTime: addMinutes(
                  newScheduledDate,
                  calendarEvent?.duration || 0
                ).toISOString(),
                timeZone: "utc",
              },
             /*  transactionId: workItemNo.toString(), */
            },
            eventId: activity.ExternalId,
          }).unwrap();
        } catch (calendarUpdateError) {
          // If calendar update fails, continue with activity update
          console.warn("Could not update calendar event:", calendarUpdateError);
        }
      }

      await updateWorkItem({
        customerNo: activity.Customer.Id,
        request: {
          scheduledDate: format(newScheduledDate, "yyyy-MM-dd'T'HH:mm:ss"),
          title: activity.Name,
          description: activity.Description,
          teamId: activity.Team.Id,
          assignedToId: activity.AssignedTo?.Id,
          externalId: activity.ExternalId,
          workNo: activity.WorkId ?? undefined,
          type: activity.Type,
          workItemNo: workItemNo,
          RegisterContactAttempt: registerContactAttempt,
        },
      }).unwrap();

      onComplete();
    } catch (err) {
      setError("Ett fel uppstod när aktiviteten skulle flyttas. Försök igen.");
      console.error("Error moving work item:", err);
    } finally {
      setIsCompleting(false);
    }
  };

  const formatCurrentDate = () => {
    if (!activity?.ScheduledTo) return "Ej schemalagd";

    const date = new Date(activity.ScheduledTo);
    return format(date, "EEEE d MMMM yyyy 'kl' HH:mm", { locale: sv });
  };

  const getSuggestedTimes = () => {
    const now = new Date();
    const tomorrow9am = new Date(now);
    tomorrow9am.setDate(now.getDate() + 1);
    tomorrow9am.setHours(9, 0, 0, 0);

    const suggestions = [
      {
        label: "Imorgon kl 09:00",
        date: tomorrow9am,
        icon: <TodayIcon fontSize="small" />,
      },
    ];

    if (activity?.ScheduledTo) {
      const currentDate = new Date(activity.ScheduledTo);
      const nextWeek = addDays(currentDate, 7);
      const nextWeekSameTime = new Date(nextWeek);

      suggestions.push({
        label: "Nästa vecka samma tid",
        date: nextWeekSameTime,
        icon: <NextWeekIcon fontSize="small" />,
      });

      const twoWeeksLater = addDays(currentDate, 14);
      suggestions.push({
        label: "Om två veckor",
        date: twoWeeksLater,
        icon: <CalendarIcon fontSize="small" />,
      });
    }

    return suggestions;
  };

  // Loading state
  if (isLoading && !activity) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #1976d2 0%, #1976d2dd 100%)",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ScheduleIcon />
            <Typography variant="h6">Flytta aktivitet i tid</Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <Fade in={true}>
              <Box sx={{ textAlign: "center" }}>
                <CircularProgress
                  size={48}
                  sx={{
                    mb: 2,
                    color: "primary.main",
                  }}
                />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Laddar aktivitet...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Hämtar schemalagd tid från servern
                </Typography>
              </Box>
            </Fade>
          </Box>

          {/* Loading Skeleton Content */}
          <Box sx={{ mt: 3 }}>
            {/* Current time skeleton */}
            <Box sx={{ mb: 3 }}>
              <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
              <Skeleton
                variant="rectangular"
                width="100%"
                height={56}
                sx={{ borderRadius: 2 }}
              />
            </Box>

            {/* Date picker skeleton */}
            <Box sx={{ mb: 3 }}>
              <Skeleton variant="text" width="20%" height={20} sx={{ mb: 1 }} />
              <Skeleton
                variant="rectangular"
                width="100%"
                height={56}
                sx={{ borderRadius: 1 }}
              />
            </Box>

            {/* Quick actions skeleton */}
            <Box sx={{ mb: 3 }}>
              <Skeleton variant="text" width="25%" height={20} sx={{ mb: 1 }} />
              <Box sx={{ display: "flex", gap: 1 }}>
                {[1, 2, 3].map((i) => (
                  <Skeleton
                    key={i}
                    variant="rectangular"
                    width={120}
                    height={32}
                    sx={{ borderRadius: 4 }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // Error state - only show if activity failed to load
  if (loadingError && !activity) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "error.main",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ErrorIcon />
            <Typography variant="h6">Kunde inte ladda aktivitet</Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Alert
            severity="error"
            sx={{
              borderRadius: 2,
              "& .MuiAlert-message": {
                width: "100%",
              },
            }}
          >
            <Typography variant="subtitle1" gutterBottom>
              Ett fel uppstod
            </Typography>
            <Typography variant="body2">
              Aktiviteten kunde inte laddas. Kontrollera din internetanslutning
              och försök igen.
            </Typography>
            <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Aktivitets-ID: {workItemNo}
              </Typography>
            </Box>
          </Alert>
        </DialogContent>
      </Dialog>
    );
  }

  // Success state - show the move dialog
  if (activity) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ScheduleIcon />
            <Typography variant="h6">Flytta aktivitet i tid</Typography>
          </Box>
          <IconButton onClick={onClose} disabled={isCompleting}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {/* Aktivitetsinformation */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: "grey.50",
              borderRadius: 2,
              mb: 3,
              border: "1px solid",
              borderColor: "grey.200",
            }}
          >
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              {activity.Name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {activity.Customer?.Name}
            </Typography>
          </Paper>

          <Typography gutterBottom>
            Välj en ny tid för denna aktivitet
          </Typography>

          {/* Nuvarande tid */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Nuvarande tid
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 2,
                bgcolor: "info.light",
                borderRadius: 2,
              }}
            >
              <AccessTimeIcon fontSize="small" />
              <Typography variant="body1" fontWeight="medium">
                {formatCurrentDate()}
              </Typography>
            </Box>
          </Box>

          {/* Ny tid väljare */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Ny tid *
            </Typography>
            <DateTimePicker
              value={newScheduledDate}
              onChange={(date) => setNewScheduledDate(date)}
              sx={{ width: "100%" }}
              minDateTime={new Date()}
              slotProps={{
                textField: {
                  variant: "outlined",
                  fullWidth: true,
                  size: "small",
                  error:
                    !newScheduledDate &&
                    !!error?.includes("Välj ett nytt datum"),
                },
              }}
            />
            <FreeSlotChips
              date={
                newScheduledDate ??
                (activity?.ScheduledTo ? new Date(activity.ScheduledTo) : null)
              }
              durationMinutes={calendarEvent?.duration || 60}
              selectedTime={newScheduledDate}
              excludeCalendarEventId={activity?.ExternalId || undefined}
              excludeWorkItemId={workItemNo}
              disabled={isCompleting}
              onSelectSlot={(slot) => setNewScheduledDate(slot)}
            />
          </Box>

          {/* Snabbval */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Snabbval
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {getSuggestedTimes().map((suggestion, index) => (
                <Chip
                  key={index}
                  label={suggestion.label}
                  icon={suggestion.icon}
                  variant="outlined"
                  clickable
                  size="small"
                  onClick={() => setNewScheduledDate(suggestion.date)}
                  sx={{
                    "&:hover": {
                      bgcolor: "primary.light",
                      color: "primary.contrastText",
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
          {/* Kontaktförsök-toggle: kunden nåddes ej */}
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: registerContactAttempt ? "#E2C275" : "grey.200",
              bgcolor: registerContactAttempt ? "#FFF8E8" : "transparent",
              transition: "all .15s ease",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={registerContactAttempt}
                  onChange={(e) => setRegisterContactAttempt(e.target.checked)}
                  sx={{
                    color: "#9B6B00",
                    "&.Mui-checked": { color: "#9B6B00" },
                  }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <ContactAttemptIcon
                    sx={{ fontSize: 18, color: "#9B6B00" }}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Kunden nåddes ej
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Loggar ett kontaktförsök på den här flytten
                    </Typography>
                  </Box>
                </Box>
              }
              sx={{ alignItems: "flex-start", m: 0 }}
            />
          </Box>

          {/*meddelande om kalenderuppdatering*/}
          {hasExternalId && !isCalendarEventNotFound && (
            <Alert
              severity="info"
              icon={<ScheduleIcon fontSize="small" />}
              sx={{ mb: 2 }}
            >
              <Typography variant="body2">
                Denna aktivitet är kopplad till en kalenderhändelse. När du
                flyttar aktiviteten kommer även kalenderhändelsen att
                uppdateras.
              </Typography>
            </Alert>
          )}
          
          {/* Warning if calendar event was deleted */}
          {hasExternalId && isCalendarEventNotFound && (
            <Alert
              severity="warning"
              icon={<ErrorIcon fontSize="small" />}
              sx={{ mb: 2 }}
            >
              <Typography variant="body2" fontWeight="medium" gutterBottom>
                Kalenderhändelsen hittades inte
              </Typography>
              <Typography variant="body2">
                Kalenderhändelsen som var kopplad till denna aktivitet finns inte längre i Outlook. 
                Endast aktiviteten kommer att flyttas.
              </Typography>
            </Alert>
          )}
          {/* Fel meddelande */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={isCompleting}>
            Avbryt
          </Button>
          <Button
            onClick={handleComplete}
            variant="contained"
            color="primary"
            disabled={isCompleting || !newScheduledDate}
            startIcon={
              isCompleting ? <CircularProgress size={20} /> : <ScheduleIcon />
            }
          >
            {isCompleting ? "Flyttar..." : "Flytta aktivitet"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Fallback
  return null;
};

export default MoveWorkItemDialog;
